import { Controller } from "@hotwired/stimulus"
import { createAlert, clearAlerts } from "../scripts/createAlert"

export default class extends Controller {
  static targets = [ "form", "fileInput", "gameFileButton", "parserSelect", "parserId", "statUrlSelectControls", "fileSelectControls" ]
  static values = {
    teamId: Number,
    isConference: Boolean,
    isXml: Boolean,
    modalId: String,
    parserId: Number
  }

  waitForTheMagicModal = null;
  xmlModal = null;
  xmlModalElement = null;
  idsContainer = null;
  gameIds = [];
  gameIndexesToAddress = []; // games where we don't know who is who
  gameIndexesToRemove = []; // files where we couldn't find any XML IDs

  connect() {
    // console.log("Hello, recap import Controller here!", this.element)
    // console.log(this.formTarget);

    this.waitForTheMagicModal = new bootstrap.Modal(document.getElementById('waitForTheMagicModal'));

    // Find the modal by dynamic ID (portal pattern)
    const modalId = this.hasModalIdValue ? this.modalIdValue : 'xml-id-helper-modal';
    this.xmlModalElement = document.getElementById(modalId);
    // debugger;

    if (this.xmlModalElement && this.isXmlValue) {
      this.xmlModal = new bootstrap.Modal(this.xmlModalElement);
      this.idsContainer = this.xmlModalElement.querySelector('.xml-ids-container');

      // Set up event handlers for the modal buttons (since modal is outside controller scope)
      this._boundClearFileInput = this.clearFileInput.bind(this);
      this._boundUpdateIdsAndReSubmit = this.updateIdsAndReSubmit.bind(this);

      this.xmlModalElement.querySelectorAll('.xml-modal-cancel-btn').forEach(btn => {
        btn.addEventListener('click', this._boundClearFileInput);
      });

      const submitBtn = this.xmlModalElement.querySelector('.xml-modal-submit-btn');
      if (submitBtn) {
        submitBtn.addEventListener('click', this._boundUpdateIdsAndReSubmit);
      }
    }

    // On Step 2 of the game, there is a select dropdown that allows the user to select a parser
    // Which is different than the Import Button on Step 1 on the games list
    // So when the page loads, we need to fire this method to set the parser's selected value
    this.submitParserSelect();
  }

  disconnect() {
    // Clean up event handlers
    if (this.xmlModalElement) {
      this.xmlModalElement.querySelectorAll('.xml-modal-cancel-btn').forEach(btn => {
        btn.removeEventListener('click', this._boundClearFileInput);
      });

      const submitBtn = this.xmlModalElement.querySelector('.xml-modal-submit-btn');
      if (submitBtn) {
        submitBtn.removeEventListener('click', this._boundUpdateIdsAndReSubmit);
      }
    }
  }

  openFileSelect() {
    this.fileInputTarget.click();
  }

  attemptSubmit(event) {
    // exit early if we are dealing with a conference that doesn't care about XML IDs
    if (this.isConferenceValue || event.target.id == 'season_file') {
      this.formTarget.submit();
      return;
    }

    // update the parser ID value if its not set
    if (this.hasParserIdTarget && this.parserIdTarget.value === "") {
      this.parserIdTarget.value = parseInt(this.parserSelectTarget.options[this.parserSelectTarget.selectedIndex].dataset['parserId']);
    }

    // exit early if this is triggered from Step 2 on the game where multiple parsers could be present to select
    if (this.hasParserSelectTarget) {

      const selectedIndex = this.parserSelectTarget.selectedIndex;
      const selectedOption = this.parserSelectTarget.options[selectedIndex];

      if (selectedOption.dataset['isXml'] == 'false' || selectedOption.dataset['isConvertedXml'] == 'true' || selectedOption.dataset['isStrictlyXml'] == 'false') {
        this.submit(event);
        return;
      }

    }

    // if we are dealing with XML files, we need to do some extra work to make sure we have the correct XML IDs
    // take all selected files and open them on the client to extract the XML IDs
    // make a request to the server to get the XML IDs for the team
    // if we see a file with XML IDs that we don't have, do NOT submit yet
    // present them the XML IDs that we have in the file and ask them to select which one is them first
    // save the selections and THEN submit the form

    const files = this.fileInputTarget.files;
    const promises = Array.from(files).map((file, index) => {
      return new Promise((resolve, reject) => {
        var reader = new FileReader();

        reader.onload = (e) => {
          this.extractIds(reader.result, index);
          resolve();
        }

        reader.onerror = (e) => {
          reject(e);
        }

        reader.readAsText(file);
      });
    });

    Promise.all(promises).then(async () => {
      // console.log('Extracted all of the IDs from the given files', this.ids);
      // debugger;

      const canContinue = await this.compareAndPossiblyUpdateIds();

      // debugger;

      if (canContinue) {
        this.waitForTheMagicModal.show();

        this.formTarget.submit();
      }

    }).catch(error => {
      console.error('Error reading files', error);
    });

  }

  // Step 2: choose XML ID checking vs a direct submit based on the selected parser.
  handleFileSelect(event) {
    if (this.hasParserSelectTarget) {
      const selectedOption = this.parserSelectTarget.options[this.parserSelectTarget.selectedIndex];
      const isStrictlyXml = selectedOption.dataset.isStrictlyXml == 'true';

      if (isStrictlyXml) {
        this.attemptSubmit(event);
      } else {
        this.submit(event);
      }
      return;
    }

    if (this.isXmlValue) {
      this.attemptSubmit(event);
    } else {
      this.submit(event);
    }
  }

  // This method runs only on Step 2 manual upload of the game file
  submitParserSelect(event = null) {

    if (!this.hasParserSelectTarget) {
      return;
    }

    const selectedOption = event ? event.target.selectedOptions[0] : this.parserSelectTarget.selectedOptions[0];
    const parserId = selectedOption.dataset.parserId;
    this.parserIdTarget.value = parserId;
    this.parserIdValue = parserId;

    if (this.hasFileInputTarget) {
      const isStrictlyXml = selectedOption.dataset.isStrictlyXml == 'true';
      const extensions = selectedOption.dataset.fileExtensions;
      this.fileInputTarget.setAttribute('accept', isStrictlyXml ? '.xml,.xtg' : (extensions || '*'));
    }

    // Toggle the file select/URL submission controls based on the parsers webBased status
    if (selectedOption.dataset.webBased == 'true') {
      if (this.hasStatUrlSelectControlsTarget) {
        this.statUrlSelectControlsTarget.classList.remove('d-none');
      }
      this.fileSelectControlsTarget.classList.add('d-none');
    } else {
      if (this.hasStatUrlSelectControlsTarget) {
        this.statUrlSelectControlsTarget.classList.add('d-none');
      }
      this.fileSelectControlsTarget.classList.remove('d-none');
    }

    // Toggle the XML ID helper modal based on the parsers isXml status
    if (selectedOption.dataset.isXml == 'true' && selectedOption.dataset.isConvertedXml != 'true') {

      // Find the modal by dynamic ID (portal pattern)
      const modalId = 'xml-id-helper-modal-' + parserId;
      this.xmlModalElement = document.getElementById(modalId);

      if (this.xmlModalElement) {
        this.xmlModal = new bootstrap.Modal(this.xmlModalElement);
        this.idsContainer = this.xmlModalElement.querySelector('.xml-ids-container');

        // Set up event handlers for the modal buttons (since modal is outside controller scope)
        this._boundClearFileInput = this.clearFileInput.bind(this);
        this._boundUpdateIdsAndReSubmit = this.updateIdsAndReSubmit.bind(this);

        this.xmlModalElement.querySelectorAll('.xml-modal-cancel-btn').forEach(btn => {
          btn.addEventListener('click', this._boundClearFileInput);
        });

        // const submitBtn = this.xmlModalElement.querySelector('.xml-modal-submit-btn');
        // if (submitBtn) {
        //   submitBtn.addEventListener('click', this._boundUpdateIdsAndReSubmit);
        // }
      }

    }
  }

  submitStatUrl(event) {
    event.preventDefault();

    const parserId = event.target.dataset.parserId;
    this.parserIdTarget.value = parserId;

    this.formTarget.submit();
  }

  extractIds(xml, index) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml,"text/xml");
    const found_ids = Array.from(xmlDoc.getElementsByTagName("team")).map(team => team['id'].trim());
    // console.log('Found IDS', found_ids);
    // debugger;

    this.gameIds.push({ index, ids: found_ids });
  }

  async compareAndPossiblyUpdateIds() {
    // debugger;
    const response = await fetch(`/entity/teams/${this.teamIdValue}/xml_ids`);
    const data = await response.json();
    // console.log('Data', data);

    let existingTeamIds = [];
    if (data.xml_ids) {
      existingTeamIds = data.xml_ids.split(',').map(id => id.trim());
    } else {
      existingTeamIds = [];
    }

    let alreadyHighlightedXmlIds = [];
    this.gameIds.forEach(({ index, ids: gameIds }) => {

      // debugger;
      if (!gameIds.some(id => existingTeamIds.includes(id)) && !gameIds.some(game_id => alreadyHighlightedXmlIds.includes(game_id))) {

        // debugger;
        // if we don't have any IDs here, the format of the file is whack and we need to just skip this file all-together
        if (gameIds.length == 0) {
          this.gameIndexesToRemove.push(index);
          this.gameIndexesToAddress.push(index);
        } else {
          // neither of the 2 ids that are used in the file are in the existingTeamIds
          // we need to present the user with the IDs that we have in the file and ask them to select which one is them first
          // save the selections and THEN submit the form
          // console.log('Game IDs', gameIds);
          this.gameIndexesToAddress.push(index);
          alreadyHighlightedXmlIds.push(...gameIds);
          // debugger;
        }
      }

    })

    // reset the modal
    let showModalAndReturn = false;
    if (this.idsContainer) {
      this.idsContainer.innerHTML = '';
    }
    clearAlerts();

    if (this.gameIndexesToAddress.length > 0 && this.gameIndexesToAddress.filter(index => !this.gameIndexesToRemove.includes(index)).length > 0) {
      // debugger;

      let gameQueryString = this.gameIndexesToAddress.filter(index => !this.gameIndexesToRemove.includes(index)).map(index => "game[" + index + "]=" + encodeURIComponent(this.gameIds.find(game => game.index == index).ids.join(',')));
      gameQueryString = gameQueryString.join('&');

      await fetch(`/game_xml_ids?${gameQueryString}`).then(res => res.text()).then(partial => {
        const idContainer = document.querySelector(`#xml-id-helper-modal-${this.parserIdValue}`);
        if (idContainer) {
          idContainer.querySelector('.xml-ids-container').insertAdjacentHTML('beforeend', partial);
        }
      });

      showModalAndReturn = true;
    }

    if (this.gameIndexesToRemove.length > 0) {
      // debugger;

      // let the user know which files are going to be removed from the form
      let fileNames = [];
      let files = this.fileInputTarget.files;
      fileNames = Array.from(files).map((file, index) => this.gameIndexesToRemove.includes(index) ? file.name : null).filter(name => name !== null);

      let element = "<div class='py-3'><p>The following files will not be submitted because we could not identify any teams:</p><ul>";
      element += fileNames.map(name => `<li>${name}</li>`).join('');
      element += "</ul>";

      this.idsContainer.insertAdjacentHTML('beforeend', element);

      showModalAndReturn = true;
    }

    if (showModalAndReturn) {
      this.xmlModal.show();

      // if we get here, we need to wait for the user to select the XML IDs
      return false;
    }

    // if we get here, we can submit the form
    return true;
  }

  async updateIdsAndReSubmit(event) {
    event.preventDefault();

    // event listeners are added because sometimes this method is called when the modal is
    // outside of the controller context.
    // we remove the listeners because we only want to run this method once
    event.target.removeEventListener('click', this._boundUpdateIdsAndReSubmit);

    // debugger;

    if (this.gameIndexesToRemove.length > 0) {
      // remove the files from the form
      const dt = new DataTransfer();

      for (let i = 0; i < this.fileInputTarget.files.length; i++) {
        if (!this.gameIndexesToRemove.includes(i)) {
          dt.items.add(this.fileInputTarget.files[i]);
        }
      }

      this.fileInputTarget.files = dt.files;
    }

    // Query for found XML ID checkboxes within the modal
    const foundXmlIdElements = this.xmlModalElement ? Array.from(this.xmlModalElement.querySelectorAll('.found-xml-id')) : [];
    let updatedIds = foundXmlIdElements.filter(elm => elm.checked).map(e => e.value).join(',');
    if (updatedIds.length > 0) {

      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }

      // add the CSRF token to the headers
      const csrf = document.querySelector('meta[name="csrf-token"]')
      if (csrf) {
        headers['X-CSRF-Token'] = csrf.content
      }

      // submit the selected IDs to the database and then re-submit the form
      await fetch(`/entity/teams/${this.teamIdValue}/xml_ids`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          entity_team: {
            xml_id: foundXmlIdElements.filter(elm => elm.checked).map(e => e.value).join(',')
          }
        })
      }).then(res => res.json()).then(data => {
        console.log('Updated the XML IDs for the team!', data);
      });
    }

    // after removing the files we need need to and updating the XML IDs for the team,
    // check to see if we have files we actually need to submit
    if (this.fileInputTarget.files.length > 0) {
      this.xmlModal.hide();
      this.waitForTheMagicModal.show();

      this.formTarget.submit();
    } else {
      this.xmlModal.hide();

      createAlert('danger', 'No files to submit.');
    }
  }

  submit(event) {
    event.preventDefault();

    if (event.target.files.length == 0) {
      return;
    }

    if (this.hasParserIdTarget && this.parserIdTarget.value === "") {
      this.parserIdTarget.value = parseInt(this.parserSelectTarget.options[this.parserSelectTarget.selectedIndex].dataset['parserId']);
    }

    this.waitForTheMagicModal.show();

    this.formTarget.submit();
  }

  clearFileInput() {

    // debugger;

    this.gameIds = [];
    this.gameIndexesToAddress = [];
    this.gameIndexesToRemove = [];

    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = '';
      this.fileInputTarget.files = new DataTransfer().files;
    }

    if (this.hasGameFileButtonTarget) {
      this.gameFileButtonTarget.innerText = 'Select';
    }

    if (this.xmlModal) {
      this.xmlModal.hide();
    }

  }
}
